import { useState } from "react";
import { ChevronsUp, CornerLeftUp } from "lucide-react";

// Numeric quick-jump rail — leap deep into a big list by % or exact row #.
// Emits onJump(offset) (page-size aligned by the parent if needed). Forward-only:
// after a jump the list starts from there; onReset returns to the top.
const STOPS = [
  { label: "Top", frac: 0 },
  { label: "25%", frac: 0.25 },
  { label: "50%", frac: 0.5 },
  { label: "75%", frac: 0.75 },
  { label: "End", frac: 0.98 },
];

export default function JumpRail({ total, atOffset = 0, onJump, onReset, testId = "jump-rail" }) {
  const [rowStr, setRowStr] = useState("");
  if (!total || total <= 50) return null; // only worth it on big lists

  const jumpFrac = (frac) => onJump(Math.max(0, Math.min(total - 1, Math.round(frac * total))));
  const jumpRow = () => {
    const n = parseInt(rowStr, 10);
    if (!Number.isFinite(n)) return;
    onJump(Math.max(0, Math.min(total - 1, n - 1)));
  };

  return (
    <div
      data-testid={testId}
      className="fixed right-2 top-1/2 -translate-y-1/2 z-[80] hidden md:flex flex-col items-stretch gap-1 p-1.5 rounded-2xl border border-white/10 bg-[#0B1120]/85 backdrop-blur-md shadow-[0_8px_30px_rgba(0,0,0,0.5)] w-[64px]"
    >
      {STOPS.map((s) => (
        <button
          key={s.label}
          type="button"
          onClick={() => jumpFrac(s.frac)}
          data-testid={`${testId}-${s.label.replace("%", "pct").toLowerCase()}`}
          className="text-[10px] font-mono py-1 rounded-lg border border-white/10 text-slate-300 hover:bg-white/10 hover:text-white transition"
        >
          {s.label}
        </button>
      ))}
      <div className="flex items-center gap-1 pt-1 border-t border-white/10">
        <input
          value={rowStr}
          onChange={(e) => setRowStr(e.target.value.replace(/[^0-9]/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter") jumpRow(); }}
          placeholder="#"
          data-testid={`${testId}-row-input`}
          className="w-full min-w-0 px-1 py-1 rounded bg-white/[0.04] border border-white/10 text-[10px] font-mono text-white text-center focus:outline-none focus:border-orange-500/50"
        />
        <button
          type="button"
          onClick={jumpRow}
          data-testid={`${testId}-row-go`}
          className="shrink-0 w-6 h-6 grid place-items-center rounded border border-white/10 text-slate-300 hover:bg-white/10 hover:text-white"
          title="Jump to row"
        >
          <ChevronsUp className="w-3.5 h-3.5 rotate-180" />
        </button>
      </div>
      {atOffset > 0 && (
        <button
          type="button"
          onClick={onReset}
          data-testid={`${testId}-back`}
          className="mt-0.5 inline-flex items-center justify-center gap-1 text-[9.5px] font-mono py-1 rounded-lg border border-orange-500/40 bg-orange-500/15 text-orange-200 hover:bg-orange-500/25"
          title="Back to start"
        >
          <CornerLeftUp className="w-3 h-3" /> Start
        </button>
      )}
    </div>
  );
}
